// utils/VerdictPunchlines.js
// 🎭 VERDICT PUNCHLINE SYSTEM WITH BASIC CLASSIFICATION
// Handles yes/no, name vs name, personal decisions, and generic A vs B trials.

const YES_WORDS = ['yes', 'yeah', 'yep', 'yup', 'sure', 'absolutely', 'of course', 'do it'];
const NO_WORDS = ['no', 'nope', 'nah', 'never', "don't", "don't do it", "dont"];
const TRUE_WORDS = ['true', 'truth'];
const FALSE_WORDS = ['false', 'lie', 'lies'];
const TRAVEL_KEYWORDS = ['been to', 'visited', 'visit', 'travel to', 'go to', 'trip to', 'vacation in', 'iceland', 'paris', 'japan', 'italy', 'hawaii', 'new york', 'london'];

// Personal decision keywords for smarter classification
const PERSONAL_KEYWORDS = ['should i', 'should we', 'am i', 'is it time', 'do i', 'help me'];
const DATING_KEYWORDS = ['text', 'ex', 'date', 'dating', 'relationship', 'shoot my shot', 'ask out', 'break up', 'dm', 'slide'];
const WORK_KEYWORDS = ['quit', 'job', 'work', 'boss', 'career', 'promotion', 'raise', 'resign'];
const STYLE_KEYWORDS = ['bangs', 'haircut', 'tattoo', 'piercing', 'dye', 'outfit', 'wear'];
const MONEY_KEYWORDS = ['buy', 'splurge', 'spend', 'purchase', 'afford', 'worth it', 'invest'];
const LIFE_KEYWORDS = ['move', 'city', 'apartment', 'pet', 'dog', 'cat', 'adopt', 'trip', 'travel'];

function norm(str = '') {
  return String(str).trim().toLowerCase();
}

function cleanTitleForTopic(title = '') {
  const t = String(title).trim();
  return t.replace(/[?!.]+$/g, '').trim();
}

function looksLikeName(label = '') {
  const text = String(label).trim();
  if (!text) return false;

  const words = text.split(/\s+/);
  if (words.length === 0 || words.length > 4) return false;

  const lettersOnly = words.every(w => /^[A-Za-z'.-]+$/.test(w));
  if (!lettersOnly) return false;

  const titleCase = words.every(w => /^[A-Z]/.test(w[0] || ''));
  return titleCase;
}

function detectPersonalCategory(title = '') {
  const t = norm(title);
  if (DATING_KEYWORDS.some(kw => t.includes(kw))) return 'dating';
  if (WORK_KEYWORDS.some(kw => t.includes(kw))) return 'work';
  if (STYLE_KEYWORDS.some(kw => t.includes(kw))) return 'style';
  if (MONEY_KEYWORDS.some(kw => t.includes(kw))) return 'money';
  if (LIFE_KEYWORDS.some(kw => t.includes(kw))) return 'life';
  return 'general';
}

function classifyTrial(trial = {}) {
  const a = norm(trial.sideA);
  const b = norm(trial.sideB);
  const titleLower = norm(trial.title || '');

  // Check for personal decision trials first (explicit trial_mode)
  if (trial.trialMode === 'decide_for_me' || trial.trial_mode === 'decide_for_me') {
    return 'personal';
  }

  // Detect implicit personal decisions by keywords
  if (PERSONAL_KEYWORDS.some(kw => titleLower.includes(kw))) {
    return 'personal';
  }

  const isYesNo =
    (YES_WORDS.includes(a) && NO_WORDS.includes(b)) ||
    (YES_WORDS.includes(b) && NO_WORDS.includes(a));

  const isTrueFalse =
    (TRUE_WORDS.includes(a) && FALSE_WORDS.includes(b)) ||
    (TRUE_WORDS.includes(b) && FALSE_WORDS.includes(a));

  if (isYesNo || isTrueFalse) {
    if (TRAVEL_KEYWORDS.some(kw => titleLower.includes(kw))) {
      return 'travelYesNo';
    }
    return 'yesNo';
  }

  if (looksLikeName(trial.sideA) && looksLikeName(trial.sideB)) return 'nameFaceoff';
  return 'generic';
}

function pick(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const idx = Math.floor(Math.random() * arr.length);
  return arr[idx];
}

// === PERSONAL DECISION PUNCHLINES (NEW!) ===
function generatePersonalDecisionPunchline({ topic, winnerLabel, loserLabel, winnerPct, style, isTie, category }) {
  const pct = Number(winnerPct) || 50;

  if (isTie) {
    const tieLines = {
      auto: [
        `The jury is split. You're on your own for this one. 🤷`,
        `50/50 deadlock. Flip a coin or ask your mom.`,
        `Hung jury. The universe says "figure it out yourself."`,
        `No consensus. Sleep on it and try again tomorrow.`
      ],
      roast: [
        `Even your friends can't decide. That's how messy this is. 😬`,
        `Deadlocked. Maybe that's the universe saying "don't."`,
        `Split decision. Your life is officially a coin flip.`,
        `The jury ghosted you with a tie. Helpful. 💀`
      ],
      internet: [
        `Jury said "bestie idk" — you're on your own. 🫠`,
        `50/50 energy. The chaos continues.`,
        `No verdict. Main character has to decide for themselves.`,
        `Tie. The algorithm can't save you now. ✨`
      ]
    };
    return pick(tieLines[style] || tieLines.auto);
  }

  // Category-specific punchlines
  if (category === 'dating') {
    if (norm(winnerLabel).includes('do it') || YES_WORDS.includes(norm(winnerLabel))) {
      const yesLines = {
        auto: [
          `${pct}% say SEND IT. Shoot your shot. 💘`,
          `The jury has spoken: GO FOR IT. What's the worst that happens?`,
          `${pct}% voted yes. The people believe in you. Now execute.`,
          `Verdict: DO IT. You've got ${pct}% of the jury behind you.`
        ],
        roast: [
          `${pct}% say send it. No excuses now. Type that message. 📱`,
          `The jury dared you. ${pct}% said go. Ball's in your court.`,
          `You asked, they answered. ${pct}% say yes. Coward if you don't.`,
          `${pct}% cleared you for launch. Don't be a 🐔`
        ],
        internet: [
          `${pct}% said send it. Manifest that text. ✨`,
          `The jury said shoot your shot. Main character energy only.`,
          `${pct}% voted yes. The universe has spoken, bestie.`,
          `Cleared for takeoff. ${pct}% believe. Now stop overthinking. 🚀`
        ]
      };
      return pick(yesLines[style] || yesLines.auto);
    } else {
      const noLines = {
        auto: [
          `${pct}% say DON'T. Put the phone down. 🚫`,
          `The jury has ruled: ABSOLUTELY NOT. Step away from the keyboard.`,
          `${pct}% voted no. They're protecting you from yourself.`,
          `Verdict: Don't do it. The jury has spoken.`
        ],
        roast: [
          `${pct}% said no. Your friends are saving you. Listen to them.`,
          `Put. The. Phone. DOWN. ${pct}% agree this is a bad idea.`,
          `${pct}% of the jury just staged an intervention.`,
          `Hard no from ${pct}% of voters. Take the L and move on.`
        ],
        internet: [
          `${pct}% said nope. That's the universe protecting you, bestie.`,
          `The jury said "pls don't" — ${pct}% are begging you.`,
          `Not today, Satan. ${pct}% said no. We love you. 💀`,
          `${pct}% voted no. Sometimes the veto is self-care. ✨`
        ]
      };
      return pick(noLines[style] || noLines.auto);
    }
  }

  if (category === 'work') {
    if (norm(winnerLabel).includes('do it') || norm(winnerLabel).includes('quit') || YES_WORDS.includes(norm(winnerLabel))) {
      const yesLines = {
        auto: [
          `${pct}% say go for it. Life's too short for "what ifs."`,
          `The jury backs the bold move. ${pct}% say yes.`,
          `Verdict: Take the leap. ${pct}% of jurors support you.`,
          `${pct}% voted for change. Trust the process.`
        ],
        roast: [
          `${pct}% say do it. Stop doom-scrolling LinkedIn and make moves.`,
          `The jury has freed you. ${pct}% say yes. Now stop asking.`,
          `${pct}% gave you permission. What are you waiting for?`,
          `${pct}% said yes. Either do it or stop complaining. 🎤⬇️`
        ],
        internet: [
          `${pct}% said bet. You only live once, bestie.`,
          `The jury cleared you for main character decisions. ${pct}% support.`,
          `${pct}% said yes. That Sunday Scaries energy? Canceled.`,
          `Jury said secure the bag differently. ${pct}% voted yes. ✨`
        ]
      };
      return pick(yesLines[style] || yesLines.auto);
    } else {
      const noLines = {
        auto: [
          `${pct}% say hold off. Maybe not the right time.`,
          `The jury urges caution. ${pct}% say wait.`,
          `Verdict: Stay put for now. ${pct}% think it's not time yet.`,
          `${pct}% voted no. Sometimes stability is the move.`
        ],
        roast: [
          `${pct}% say chill. Don't rage-quit just yet.`,
          `The jury says wait. ${pct}% think you're being dramatic.`,
          `${pct}% voted no. Maybe make a spreadsheet first?`,
          `Hold your horses. ${pct}% say this isn't the play. 📊`
        ],
        internet: [
          `${pct}% said not yet. The economy is economying, bestie.`,
          `Jury says pause. ${pct}% are being responsible adults rn.`,
          `${pct}% voted wait. Sometimes the move is no move. 🧘`,
          `Not the time. ${pct}% said chill. Trust the process.`
        ]
      };
      return pick(noLines[style] || noLines.auto);
    }
  }

  if (category === 'style') {
    if (norm(winnerLabel).includes('do it') || YES_WORDS.includes(norm(winnerLabel))) {
      return pick({
        auto: [
          `${pct}% say YES. It's just hair/skin/style — it grows back (mostly).`,
          `The jury approves the glow-up. ${pct}% voted yes.`,
          `Verdict: DO IT. ${pct}% want to see the transformation.`,
          `${pct}% cleared you. YOLO your look. 💇`
        ],
        roast: [
          `${pct}% dare you. Do it, coward. It's just ✨aesthetic choices✨.`,
          `The jury wants chaos. ${pct}% say change it up.`,
          `${pct}% said yes. Worst case: a hat for 3 months.`,
          `${pct}% approved. No regrets, only stories. 💀`
        ],
        internet: [
          `${pct}% said slay. New era incoming. ✨`,
          `Jury cleared the vibe shift. ${pct}% support the glow-up.`,
          `${pct}% voted yes. Main character transformation arc unlocked.`,
          `The people have spoken: ${pct}% say send it. Serve looks. 💅`
        ]
      }[style] || []);
    } else {
      return pick({
        auto: [
          `${pct}% say wait. Sleep on it one more night.`,
          `The jury urges pause. ${pct}% think you're good as is.`,
          `Verdict: Maybe not right now. ${pct}% aren't sold yet.`,
          `${pct}% voted no. You're already serving.`
        ],
        roast: [
          `${pct}% say don't. They've seen your Pinterest boards.`,
          `The jury intervened. ${pct}% think you'll regret it.`,
          `${pct}% said no. Screenshot this for your future self.`,
          `Hold up. ${pct}% think this is an impulsive era. 🛑`
        ],
        internet: [
          `${pct}% said bestie no. You're already iconic.`,
          `Jury says chill. ${pct}% think you're perfect as is. ✨`,
          `${pct}% voted no. Sometimes the vibe is *not* changing.`,
          `The people have spoken: ${pct}% say you're already that girl/guy.`
        ]
      }[style] || []);
    }
  }

  if (category === 'money') {
    if (norm(winnerLabel).includes('do it') || norm(winnerLabel).includes('buy') || YES_WORDS.includes(norm(winnerLabel))) {
      return pick({
        auto: [
          `${pct}% say treat yourself. You've earned it.`,
          `The jury approved the purchase. ${pct}% say go for it.`,
          `Verdict: BUY IT. ${pct}% think you deserve nice things.`,
          `${pct}% voted yes. Life's short. Get the thing. 🛍️`
        ],
        roast: [
          `${pct}% enabled you. Your bank account can @ them later.`,
          `The jury said yes. ${pct}% are bad influences and we love it.`,
          `${pct}% approved. Financial responsibility? Don't know her.`,
          `${pct}% voted yes. You were gonna do it anyway. 💸`
        ],
        internet: [
          `${pct}% said treat yourself. It's called self-care, bestie. ✨`,
          `Jury cleared the splurge. ${pct}% said secure the bag (contents).`,
          `${pct}% voted yes. You can't take it with you!`,
          `${pct}% enabled. The cart has been blessed. Proceed. 🛒`
        ]
      }[style] || []);
    } else {
      return pick({
        auto: [
          `${pct}% say save your money. Adulting wins this round.`,
          `The jury voted with your wallet. ${pct}% say no.`,
          `Verdict: Skip it. ${pct}% think you'll survive without it.`,
          `${pct}% voted no. Future you says thanks.`
        ],
        roast: [
          `${pct}% said no. Your credit card breathes a sigh of relief.`,
          `The jury protected your bank account. ${pct}% say chill.`,
          `${pct}% voted no. It'll be on sale later anyway.`,
          `Hard no from ${pct}%. Touch grass instead. 💀`
        ],
        internet: [
          `${pct}% said wait. Delayed gratification is hot rn. ✨`,
          `Jury said no. ${pct}% are financially responsible icons.`,
          `${pct}% voted skip. The universe says rent is due.`,
          `${pct}% protected your wallet. Self-control era. 💅`
        ]
      }[style] || []);
    }
  }

  // Generic personal decision fallback
  if (norm(winnerLabel).includes('do it') || YES_WORDS.includes(norm(winnerLabel))) {
    return pick({
      auto: [
        `${pct}% of the jury says DO IT. The people have spoken.`,
        `Verdict: YES. ${pct}% back your decision. Go for it.`,
        `The jury ruled ${pct}% in favor. You have permission.`,
        `${pct}% voted yes. Stop overthinking and execute.`
      ],
      roast: [
        `${pct}% said yes. You asked, they answered. Now do it.`,
        `The jury freed you. ${pct}% approved. No more excuses.`,
        `${pct}% gave you the green light. Coward if you don't. 🏃`,
        `${pct}% voted yes. Either do it or stop asking people.`
      ],
      internet: [
        `${pct}% said yes. Main character energy activated. ✨`,
        `The jury cleared you. ${pct}% support this choice.`,
        `${pct}% voted yes. The universe has spoken, bestie.`,
        `${pct}% said bet. No notes. Execute. 🚀`
      ]
    }[style] || []);
  } else {
    return pick({
      auto: [
        `${pct}% of the jury says DON'T. Heed their wisdom.`,
        `Verdict: NO. ${pct}% think this isn't the move.`,
        `The jury ruled ${pct}% against. Maybe sleep on it.`,
        `${pct}% voted no. Sometimes the answer is wait.`
      ],
      roast: [
        `${pct}% said no. They're protecting you from yourself.`,
        `The jury blocked you. ${pct}% saw where this was going.`,
        `${pct}% voted against. Take the hint. 💀`,
        `Hard no from ${pct}%. Your friends know best.`
      ],
      internet: [
        `${pct}% said nope. The universe is sending signs, bestie.`,
        `Jury said not today. ${pct}% are looking out for you.`,
        `${pct}% voted no. Sometimes self-care is self-restraint. ✨`,
        `${pct}% blocked this choice. Trust the process.`
      ]
    }[style] || []);
  }
}

// === YES/NO STYLE PUNCHLINES ===
function generateYesNoPunchline({ topic, winnerLabel, loserLabel, winnerPct, style, isTie }) {
  if (isTie) {
    const tieLines = {
      auto: [
        `Hung jury on ${topic}. Chaos respectfully continues.`,
        `The court shrugs. ${topic} stays undecided.`,
        `No clear verdict on ${topic} — appeal in the group chat.`
      ],
      roast: [
        `${topic} was so mid the jury couldn't even pick a side.`,
        `Total gridlock on ${topic}. Everyone talked, nobody listened.`,
        `The jury benched itself on ${topic}. Embarrassing, honestly.`
      ],
      internet: [
        `Jury said "idk lol" on ${topic}.`,
        `${topic} is giving "it's complicated."`,
        `${topic} remains un-Canon™. Timeline stays messy.`
      ]
    };
    return pick(tieLines[style] || tieLines.auto);
  }

  const lines = {
    auto: [
      `The jury says ${winnerLabel.toUpperCase()}: ${topic}.`,
      `${winnerPct}% of jurors stamped ${topic} with a big ${winnerLabel.toUpperCase()}.`,
      `${topic}? Court rules ${winnerLabel.toUpperCase()} and moves on.`
    ],
    roast: [
      `${winnerLabel.toUpperCase()} just steamrolled ${loserLabel.toUpperCase()} — ${topic} is settled.`,
      `${winnerPct}% of the jury sided with ${winnerLabel.toUpperCase()}. ${loserLabel.toUpperCase()} can pack it up.`,
      `${topic}: ${winnerLabel.toUpperCase()} cooked, ${loserLabel.toUpperCase()} got sent home.`
    ],
    internet: [
      `${winnerLabel.toUpperCase()} gang pulled up and made ${topic} canon.`,
      `${topic} got ratio'd into ${winnerLabel.toUpperCase()} with ${winnerPct}% of the vote.`,
      `${winnerLabel.toUpperCase()} speed-ran ${loserLabel.toUpperCase()} on ${topic}. GG, no rematch.`
    ]
  };

  return pick(lines[style] || lines.auto);
}

// === NAME vs NAME PUNCHLINES ===
function generateNameFaceoffPunchline({ topic, winnerName, loserName, winnerPct, style, isTie }) {
  if (isTie) {
    const tieLines = {
      auto: [
        `The jury was split between ${winnerName} and ${loserName}. Hung jury.`,
        `Court couldn't pick between ${winnerName} and ${loserName} — mistrial.`,
        `${winnerName} vs ${loserName} ends in a stalemate.`
      ],
      roast: [
        `${winnerName} vs ${loserName} and somehow they both fumbled. Tie game.`,
        `Jury stared at ${winnerName} and ${loserName} and said "nah, you figure it out."`,
        `Zero clutch gene detected — ${winnerName} and ${loserName} tied.`
      ],
      internet: [
        `${winnerName} vs ${loserName} ended 1–1. Twitter will decide the real winner.`,
        `Timeline said "both" — ${winnerName} and ${loserName} tied.`,
        `The jury soft-launched a tie between ${winnerName} and ${loserName}.`
      ]
    };
    return pick(tieLines[style] || tieLines.auto);
  }

  const lines = {
    auto: [
      `The jury sides with ${winnerName}. ${loserName} may appeal to the comments.`,
      `${winnerPct}% of jurors crowned ${winnerName} over ${loserName}.`,
      `${winnerName} wins this round. ${loserName} lives to fight another trial.`
    ],
    roast: [
      `${winnerName} just packed up ${loserName} in open court.`,
      `${winnerPct}% of the jury left ${loserName} on read and went with ${winnerName}.`,
      `${loserName} showed up, but ${winnerName} stole the whole case.`
    ],
    internet: [
      `${winnerName} cleared ${loserName}. No notes.`,
      `${winnerName} farming W's while ${loserName} holds the L.`,
      `Jury said "we ride with ${winnerName}." ${loserName} can log off.`
    ]
  };

  return pick(lines[style] || lines.auto);
}

// === GENERIC A vs B PUNCHLINES ===
function generateGenericPunchline({ topic, sideA, sideB, winnerLabel, loserLabel, winnerPct, style, isTie }) {
  if (isTie) {
    const tieLines = {
      auto: [
        `The jury deadlocked on ${topic}.`,
        `${sideA} vs ${sideB} ends in a perfect tie.`,
        `No clear winner on ${topic} — call it a hung jury.`
      ],
      roast: [
        `${sideA} vs ${sideB} and somehow they both tied. Mid vs mid.`,
        `Jury looked at ${sideA} and ${sideB} and said "eh, same energy."`,
        `Tie game. ${sideA} and ${sideB} equally unserious.`
      ],
      internet: [
        `${topic} ends in a 50/50 split. Democracy is drunk.`,
        `Jury couldn't pick between ${sideA} and ${sideB}. That's on y'all.`,
        `${sideA} vs ${sideB} ended in pure chaos energy: tie.`
      ]
    };
    return pick(tieLines[style] || tieLines.auto);
  }

  const pct = Number(winnerPct) || 0;
  const isUnanimous = pct >= 95;
  const isLandslide = pct >= 80;
  const isNailbiter = pct <= 55;

  if (isUnanimous) {
    const unanimous = {
      auto: [`Unanimous. ${winnerLabel} wins. Case closed.`, `The jury went 100%. ${winnerLabel}. No notes.`],
      roast: [`Unanimous. ${loserLabel} got sent home with the quickness.`, `100% vote. ${loserLabel} never stood a chance.`],
      internet: [`Unanimous. ${winnerLabel} cleared. 🧹`, `100% — ${winnerLabel} is canon now.`]
    };
    return pick(unanimous[style] || unanimous.auto);
  }

  if (isLandslide) {
    const landslide = {
      auto: [`${winnerLabel} wins in a landslide (${pct}%).`, `${winnerLabel} takes it comfortably.`],
      roast: [`${winnerLabel} steamrolled ${loserLabel} (${pct}%).`, `${loserLabel} got cooked… badly.`],
      internet: [`${winnerLabel} just spawn-camped ${loserLabel} (${pct}%).`, `${winnerLabel} farming W's.`]
    };
    return pick(landslide[style] || landslide.auto);
  }

  if (isNailbiter) {
    const close = {
      auto: [`Split decision — ${winnerLabel} by a hair.`, `Close call, but ${winnerLabel} edges it.`],
      roast: [`Barely. ${winnerLabel} survives, ${loserLabel} malding.`, `This was a photo finish. Painful.`],
      internet: [`Nailbiter. ${winnerLabel} wins on vibes + 1 juror.`, `${winnerLabel} wins… but like, barely.`]
    };
    return pick(close[style] || close.auto);
  }

  const lines = {
    auto: [
      `${winnerLabel} takes the win on ${topic}.`,
      `${winnerPct}% of jurors ruled in favor of ${winnerLabel}.`,
      `On ${topic}, the court officially sides with ${winnerLabel}.`
    ],
    roast: [
      `${winnerLabel} cooked ${loserLabel} in front of the whole jury.`,
      `${loserLabel} showed up with vibes, ${winnerLabel} showed up with receipts.`,
      `${winnerLabel} ran ${loserLabel} out of the courtroom with ${winnerPct}% of the vote.`
    ],
    internet: [
      `${winnerLabel} just speed-ran ${loserLabel} for ${winnerPct}% of the jury.`,
      `${winnerLabel} farming W's while ${loserLabel} refreshes the poll.`,
      `Jury said "we outside with ${winnerLabel}," ${loserLabel} can stay home.`
    ]
  };

  return pick(lines[style] || lines.auto);
}

function generateTravelYesNoPunchline({ topic, winnerLabel, loserLabel, winnerPct, style, isTie }) {
  if (isTie) {
    return pick([
      `Jury's split on ${topic} — half packed bags, half stayed home. Hung jury! ✈️🏠`,
      `${topic}: The court couldn't decide. Passport stays dusty. 🤷`,
      `Deadlock on ${topic}. Everyone's itinerary is chaos.`
    ]);
  }

  const baseLines = [
    `${winnerLabel.toUpperCase()} gang's booking flights — ${winnerPct}% have been! ✈️`,
    `${winnerPct}% said ${winnerLabel.toUpperCase()} to ${topic}. Pack your bags!`,
    `The jury's verdict: ${winnerLabel.toUpperCase()}! Time to explore.`,
    `${winnerLabel.toUpperCase()} wins — adventure calls ${winnerPct}% of jurors! 🌍`,
    `${loserLabel.toUpperCase()} got left behind. ${winnerPct}% chose ${winnerLabel.toUpperCase()}!`,
  ];

  if (style === 'roast') {
    return pick([
      `${loserLabel.toUpperCase()} caught an L — ${winnerPct}% are living that travel life. Stay home! 💀`,
      `${winnerLabel.toUpperCase()} crushed it. ${winnerPct}% are explorers!`,
      `${loserLabel.toUpperCase()}? More like No-go. ${winnerPct}% said ${winnerLabel.toUpperCase()}!`,
    ]);
  }

  if (style === 'internet') {
    return pick([
      `${winnerLabel.toUpperCase()} speedran that trip at ${winnerPct}%. Noobs stay home. GG ✈️🔥`,
      `Jury ratio'd ${loserLabel.toUpperCase()} into oblivion. ${topic} canon: ${winnerLabel.toUpperCase()} 🏆`,
      `${winnerPct}% said yes! Wanderlust wins! 🌌`,
    ]);
  }

  return pick(baseLines);
}

/**
 * Main entry point
 *
 * @param {Object} trial  - { title, sideA, sideB, trialMode, decisionCategory, ... }
 * @param {Object} verdict - { winner: 'A' | 'B' | 'tie' | null, winnerPct?: number }
 * @param {('auto'|'roast'|'internet')} style
 */
export function generatePunchline(trial = {}, verdict = {}, style = 'roast') {
  try {
    const safeStyle = ['auto', 'roast', 'internet'].includes(style) ? style : 'auto';

    const type = classifyTrial(trial);
    const winnerKey = verdict?.winner;
    const isTie = !winnerKey || winnerKey === 'tie';

    const sideA = trial.sideA || 'Side A';
    const sideB = trial.sideB || 'Side B';
    const topic = cleanTitleForTopic(trial.title || `${sideA} vs ${sideB}`);

    const winnerLabel = winnerKey === 'B' ? sideB : sideA;
    const loserLabel = winnerKey === 'B' ? sideA : sideB;
    const winnerPct = typeof verdict?.winnerPct === 'number'
      ? verdict.winnerPct
      : verdict?.percentage || null;

    // NEW: Handle personal decision trials
    if (type === 'personal') {
      const category = trial.decisionCategory || trial.decision_category || detectPersonalCategory(trial.title);
      return generatePersonalDecisionPunchline({
        topic,
        winnerLabel,
        loserLabel,
        winnerPct: winnerPct || 50,
        style: safeStyle,
        isTie,
        category
      });
    }

    if (type === 'travelYesNo') {
      return generateTravelYesNoPunchline({
        topic,
        winnerLabel,
        loserLabel,
        winnerPct: winnerPct || 66.7,
        style: safeStyle,
        isTie
      });
    }

    if (type === 'yesNo') {
      const yesLabel = YES_WORDS.includes(norm(sideA)) ? sideA : YES_WORDS.includes(norm(sideB)) ? sideB : winnerLabel;
      const noLabel = NO_WORDS.includes(norm(sideA)) ? sideA : NO_WORDS.includes(norm(sideB)) ? sideB : loserLabel;

      return generateYesNoPunchline({
        topic,
        winnerLabel: isTie ? yesLabel : winnerLabel,
        loserLabel: isTie ? noLabel : loserLabel,
        winnerPct: winnerPct || 0,
        style: safeStyle,
        isTie
      });
    }

    if (type === 'nameFaceoff') {
      const winnerName = winnerKey === 'B' ? sideB : sideA;
      const loserName = winnerKey === 'B' ? sideA : sideB;

      return generateNameFaceoffPunchline({
        topic,
        winnerName,
        loserName,
        winnerPct: winnerPct || 0,
        style: safeStyle,
        isTie
      });
    }

    // Fallback: generic A vs B / preference questions
    return generateGenericPunchline({
      topic,
      sideA,
      sideB,
      winnerLabel,
      loserLabel,
      winnerPct: winnerPct || 0,
      style: safeStyle,
      isTie
    });
  } catch (err) {
    console.error('generatePunchline error:', err);
    const fallbackTitle = trial?.title || 'The jury has spoken.';
    return fallbackTitle;
  }
}

// === COMPATIBILITY HELPERS ====================================

export function generateMultiplePunchlines(trial, verdict, count = 3) {
  const styles = ['auto', 'roast', 'internet'];
  const punchlines = [];

  for (let i = 0; i < count; i++) {
    const style = styles[i % styles.length];
    const text = generatePunchline(trial, verdict, style);
    punchlines.push({
      text,
      style,
      id: `${style}_${i}`
    });
  }

  return punchlines;
}

export function getFallbackPunchline(trial, verdict) {
  return generatePunchline(trial, verdict, 'auto');
}
